import { Breadcrumb } from '@/components/atoms';
import { Pagination } from '@/components/atoms/Pagination/Pagination';
import { PostCard } from '@/components/molecules';
import { decodeHtmlEntities } from '@/utils/decodeHtmlEntities';
import { sanitizeLinkHref } from '@/utils/sanitizeLinkHref';
import type { AppData } from '@/types/app';
import './Archive.scss';

interface ArchiveProps {
  data: AppData;
}

export function Archive({ data }: ArchiveProps) {
  const { post, archive, related_posts, pagination } = data;
  const posts = related_posts ?? [];
  const currentPage = pagination?.current_page ?? 1;
  const totalPages = pagination?.total_pages ?? 1;

  const archiveType = archive?.type ?? 'home';
  const isHome = archiveType === 'home';

  const rawTitle = decodeHtmlEntities(archive?.title ?? post?.title ?? '');
  const title = archiveType === 'tag' ? `Étiquette : ${rawTitle}` : rawTitle;
  const description = archive?.description ?? '';

  const safeLinks = (archive?.links ?? [])
    .map((link) => ({ ...link, safeUrl: sanitizeLinkHref(link.url) }))
    .filter(
      (link): link is typeof link & { safeUrl: string } =>
        link.safeUrl !== null,
    );

  const breadcrumbItems = isHome
    ? [
        { name: 'Actualités', ...(currentPage > 1 ? { href: '/actualites/' } : {}) },
        ...(currentPage > 1 ? [{ name: `Page ${currentPage}` }] : []),
      ]
    : (archive?.breadcrumbs && archive.breadcrumbs.length > 0)
      ? archive.breadcrumbs.map((b) => ({
          name: decodeHtmlEntities(b.name),
          ...(b.href ? { href: b.href } : {}),
        }))
      : [
          { name: decodeHtmlEntities(archive?.title ?? '') },
        ];

  const archiveSlug = archive?.slug ?? '';
  const paginationBasePath =
    archive?.path
    ?? (archiveType === 'home'
      ? '/actualites/'
      : archiveType === 'tag'
        ? `/tag/${archiveSlug}/`
        : archiveType === 'author'
          ? `/auteur/${archiveSlug}/`
          : `/${archiveSlug}/`);

  return (
    <div className="archive">
      <header className="archive__header">
        {archiveType !== 'tag' && archiveType !== 'author' && (
          <Breadcrumb items={breadcrumbItems} />
        )}
        <h1 className="archive__title">{title}</h1>
        {description && (
          <div
            className="archive__description"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
        {safeLinks.length > 0 && (
          <div className="archive__quick-links">
            {safeLinks.map((link) => (
              <a
                key={link.title}
                href={link.safeUrl}
                className="archive__quick-link"
              >
                {link.title}
              </a>
            ))}
          </div>
        )}
      </header>

      {posts.length > 0 && (
        <div className="post-card-grid">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} />
          ))}
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        basePath={paginationBasePath}
      />
    </div>
  );
}
